import { useState, useRef, useEffect } from "react";
import { SelectOption } from "./Select";

interface CreditTypeOptions {
  performing: SelectOption[];
  composition_and_lyrics: SelectOption[];
  production_and_engineering: SelectOption[];
}

interface RoleSearchInputProps {
  value: string;
  creditTypeOptions: CreditTypeOptions;
  onChange: (value: string) => void;
  placeholder?: string;
  selectedValues?: string[];
}

const GROUP_LABELS: Record<keyof CreditTypeOptions, string> = {
  performing: "Performing",
  composition_and_lyrics: "Composition & Lyrics",
  production_and_engineering: "Production & Engineering",
};

const GROUP_KEYS: (keyof CreditTypeOptions)[] = [
  "performing",
  "composition_and_lyrics",
  "production_and_engineering",
];

const RoleSearchInput: React.FC<RoleSearchInputProps> = ({
  value,
  creditTypeOptions,
  onChange,
  placeholder = "Search for a role",
  selectedValues = [],
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const highlightedRef = useRef<HTMLLIElement>(null);

  const selectedLabel = (() => {
    for (const key of GROUP_KEYS) {
      const found = creditTypeOptions[key].find((o) => o.value === value);
      if (found) return found.label;
    }
    return "";
  })();

  const filteredGroups = GROUP_KEYS.map((key) => ({
    key,
    label: GROUP_LABELS[key],
    options: creditTypeOptions[key].filter(
      (o) =>
        o.label.toLowerCase().includes(query.toLowerCase()) &&
        !selectedValues.includes(o.value),
    ),
  })).filter((g) => g.options.length > 0);

  const flatOptions = filteredGroups.flatMap((g) => g.options);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  useEffect(() => {
    highlightedRef.current?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const handleSelect = (option: SelectOption) => {
    onChange(option.value);
    setQuery("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || flatOptions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((i) => (i + 1) % flatOptions.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(
          (i) => (i - 1 + flatOptions.length) % flatOptions.length,
        );
        break;
      case "Enter":
        e.preventDefault();
        handleSelect(flatOptions[highlightedIndex]);
        break;
      case "Escape":
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  const showDropdown = isOpen && flatOptions.length > 0;

  let flatIndex = 0;

  return (
    <div className="relative flex w-full flex-col gap-2">
      <input
        ref={inputRef}
        type="text"
        value={isOpen ? query : selectedLabel}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          setQuery("");
          setIsOpen(true);
        }}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="border-cream-500 placeholder-cream-600 selection:bg-peach-300 dark:placeholder-cream-500 text-cream-950 dark:bg-cream-800 dark:text-cream-100 w-full rounded border font-medium"
        autoComplete="off"
      />
      {showDropdown && (
        <ul className="border-cream-300 dark:border-cream-700 dark:bg-cream-900 absolute top-full left-0 z-10 mt-1 max-h-60 w-full overflow-auto rounded border bg-white shadow-lg">
          {filteredGroups.map((group) => {
            const items = group.options.map((option) => {
              const currentIndex = flatIndex++;
              return (
                <li
                  key={option.value}
                  ref={currentIndex === highlightedIndex ? highlightedRef : null}
                  onMouseDown={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(currentIndex)}
                  className={`cursor-pointer px-3 py-2 ${
                    currentIndex === highlightedIndex
                      ? "bg-peach-100 dark:bg-cream-800"
                      : "hover:bg-cream-50 dark:hover:bg-cream-800"
                  }`}
                >
                  {option.label}
                </li>
              );
            });

            return (
              <li key={group.key}>
                <div className="text-cream-600 dark:text-cream-400 px-3 pt-3 pb-1 text-xs font-bold uppercase tracking-wide">
                  {group.label}
                </div>
                <ul>{items}</ul>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export type { CreditTypeOptions };
export default RoleSearchInput;
